import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, NgZone, output, signal } from '@angular/core';
import { DatePipe, NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-navbar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, NgOptimizedImage, DatePipe],
    host: {
        '(document:click)': 'onDocumentClick($event)',
    },
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
    private readonly zone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    readonly sidebarCollapsed = input(false);
    readonly toggleSidebar = output<void>();
    authService = inject(AuthService);
    readonly menuOpen = signal(false);

    /**
     * Horloge du header, rafraîchie chaque seconde.
     *
     * La minuterie tourne **hors de la zone Angular** : l'application conserve `zone.js`
     * (voir CLAUDE.md), où un `setInterval` laissé dans la zone déclencherait un cycle de
     * détection sur tout l'arbre à chaque seconde. L'écriture du signal suffit à redessiner la
     * seule navbar. Arrêt à la destruction du composant : sans cela, la minuterie survivrait au
     * composant et ferait échouer toute future spec.
     */
    readonly maintenant = signal(new Date());

    constructor() {
        this.zone.runOutsideAngular(() => {
            const minuterie = setInterval(() => this.maintenant.set(new Date()), 1000);
            this.destroyRef.onDestroy(() => clearInterval(minuterie));
        });
    }

    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        if (this.menuOpen() && !target.closest('.relative')) {
            this.menuOpen.set(false);
        }
    }

    userInitials(): string {
        const user = this.authService.user();

        if (!user) {
            return '?';
        }
        const first = user.first_name?.[0] ?? '';
        const last = user.last_name?.[0] ?? '';

        return (first + last).toUpperCase() || user.username?.[0]?.toUpperCase() || '?';
    }

    userDisplayName(): string {
        const user = this.authService.user();

        if (!user) {
            return 'Utilisateur';
        }
        const parts = [user.first_name, user.last_name].filter(Boolean);

        return parts.length > 0 ? parts.join(' ') : user.username;
    }

    userEmail(): string {
        return this.authService.user()?.email ?? '';
    }

    userRole(): string {
        const user = this.authService.user();

        if (!user) {
            return '';
        }
        const firstRole = user.roles?.[0]?.libelle;
        if (firstRole) {
            return firstRole;
        }

        return user.is_superuser ? 'Super Admin' : 'Utilisateur';
    }

    onLogout(): void {
        this.menuOpen.set(false);
        this.authService.logout();
    }
}
